import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import L from 'leaflet';
import { getJobsiteCoords } from './useJobsite';

const SAVED_PARCEL_INTEL_KEY = 'siteTakeoffV3_savedParcelIntel';
const POINT_REVERSE_GEOCODE_CACHE_KEY = 'siteTakeoffV3_pointReverseGeocodeCache_v1';
const POINT_REVERSE_GEOCODE_CACHE_PRECISION = 5;
const POINT_REVERSE_GEOCODE_CACHE_MAX_ITEMS = 1200;

// (Re)attach a hook-owned feature group lazily: MapContainer recreates the
// Leaflet map on Estimate-tab roundtrips, so a group created once at mount
// would be left on the destroyed map.
function ensureFeatureGroup(map, ref) {
  let group = ref.current;
  if (!group || !map.hasLayer(group)) {
    group = new L.FeatureGroup();
    map.addLayer(group);
    ref.current = group;
  }
  return group;
}

/**
 * Loads and renders property lines for a jobsite.
 * Sources (in priority order):
 *   1. Backend getAPNAndParcelInfo (AspireProperty / parcel data)
 *   2. Manual property lines (user-drawn overrides)
 *   3. ESRI Maricopa County fallback (direct point-in-polygon query)
 *
 * Also supports drawing new custom property lines.
 */

// Whether the county layer should draw. Custom (manual) lines exist because
// the county parcel is wrong for the jobsite — read-only viewers opt in via
// manualOverridesCounty so custom lines REPLACE county lines instead of
// stacking. SiteTakeoffV3 keeps the default (both layers + its own per-type
// visibility toggles). Only custom lines that will actually render (>= 3
// points, mirroring the render effect's skip) suppress the county layer.
export function shouldRenderCountyLines({ countyVisible, manualOverridesCounty = false, manualLines = [] }) {
  if (!countyVisible) return false;
  if (!manualOverridesCounty) return true;
  return !manualLines.some(line => (line?.paths?.length || 0) >= 3);
}

export default function usePropertyLines({
  url,
  jobsite,
  mapRef,
  visible = true,
  parcelInspectorEnabled = false,
  manualOverridesCounty = false,
  redrawKey,
}) {
  const [countyLines, setCountyLines] = useState([]);
  const [manualLines, setManualLines] = useState([]);
  const [usingFallback, setUsingFallback] = useState(false);
  const [countyVisible, setCountyVisible] = useState(true);
  const [manualVisible, setManualVisible] = useState(true);
  const layerGroupRef = useRef(null);
  const contextLayerGroupRef = useRef(null);
  const parcelContextCacheRef = useRef({});
  const parcelPointContextCacheRef = useRef({});
  const pointReverseGeocodeCacheRef = useRef(loadPointReverseGeocodeCache());
  const parcelIntelLabelsRef = useRef({});

  // Remove the layer groups on unmount
  useEffect(() => {
    return () => {
      const map = mapRef.current;
      if (map && layerGroupRef.current) {
        try { map.removeLayer(layerGroupRef.current); } catch (_) {}
      }
      if (map && contextLayerGroupRef.current) {
        try { map.removeLayer(contextLayerGroupRef.current); } catch (_) {}
      }
      layerGroupRef.current = null;
      contextLayerGroupRef.current = null;
    };
  }, [mapRef]);

  // Load county + manual property lines for a jobsite
  const loadLines = useCallback(async () => {
    if (!url || !jobsite?._id) {
      setCountyLines([]);
      setManualLines([]);
      setUsingFallback(false);
      return;
    }
    const siteId = jobsite._id;
    try {
      const resp = await axios.get(
        `${url}/api/customer/getAPNAndParcelInfo?id=${siteId}&includeManual=true`
      );

      const county = [];
      const data = resp?.data;

      const propertyLinePayload = Array.isArray(data?.propertyLines)
        ? data.propertyLines
        : Array.isArray(data)
          ? data
          : [];

      if (propertyLinePayload.length) {
        propertyLinePayload
          .filter(p => p?.googleMapsFormat?.length > 0)
          .forEach((property, idx) => {
            const props = property.properties || {};
            const apn = props.APN || property.APN || '';
            county.push({
              id: `county_${siteId}_${apn || property._id || idx}`,
              APN: apn,
              address: props.address || property.address || '',
              acres: props.acres ?? property.acres ?? property.area ?? null,
              sf: props.sf ?? property.sf ?? null,
              paths: property.googleMapsFormat.map(p => ({ lat: p.lat, lng: p.lng })),
              source: property.source || props.source || 'county',
              properties: {
                ...props,
                APN: apn,
                address: props.address || property.address || '',
              },
            });
          });
      }

      setCountyLines(county);
      setUsingFallback(false);

      // If nothing returned, try ESRI fallback
      if (county.length === 0) {
        const coords = getJobsiteCoords(jobsite);
        if (coords) {
          const fallback = await fetchESRIFallback(siteId, coords[0], coords[1]);
          if (fallback) {
            setCountyLines([fallback]);
            setUsingFallback(true);
          }
        }
      }

      // Load manual lines
      const manualResp = await axios.get(
        `${url}/api/customer/manual-property-lines?jobSiteId=${siteId}`
      );
      if (manualResp.data?.success && Array.isArray(manualResp.data.lines)) {
        setManualLines(manualResp.data.lines);
      } else {
        setManualLines([]);
      }
    } catch (err) {
      console.error('Error loading property lines:', err);
      // Try fallback on error too
      try {
        const coords = getJobsiteCoords(jobsite);
        if (coords) {
          const fallback = await fetchESRIFallback(siteId, coords[0], coords[1]);
          if (fallback) {
            setCountyLines([fallback]);
            setUsingFallback(true);
          }
        }
      } catch (_) {}
    }
  }, [url, jobsite]);

  useEffect(() => { loadLines(); }, [loadLines]);

  // Render property lines — respects per-type visibility
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const group = ensureFeatureGroup(map, layerGroupRef);
    group.clearLayers();

    if (!visible) return;

    if (shouldRenderCountyLines({ countyVisible, manualOverridesCounty, manualLines })) {
      countyLines.forEach((line) => {
        if (!line.paths?.length) return;
        const coords = line.paths.map(p => [p.lat, p.lng]);
        const layer = L.polygon(coords, {
          color: '#4caf50',
          weight: 3,
          opacity: 0.8,
          fillColor: '#4caf50',
          fillOpacity: 0.05,
          interactive: false,
        });
        group.addLayer(layer);
      });
    }

    if (manualVisible) {
      manualLines.forEach((line) => {
        if (!line.paths?.length || line.paths.length < 3) return;
        const coords = line.paths.map(p => [p.lat, p.lng]);
        const layer = L.polygon(coords, {
          color: '#2e7d32',
          weight: 3,
          opacity: 0.9,
          fillColor: '#4caf50',
          fillOpacity: 0.08,
          dashArray: '6, 4',
          interactive: false,
        });
        layer.bindTooltip(`<strong>${line.name || 'Custom Property Line'}</strong><br/><em>Manual</em>`);
        group.addLayer(layer);
      });
    }
  }, [countyLines, manualLines, visible, countyVisible, manualVisible, manualOverridesCounty, mapRef, url, redrawKey]);

  // Right-click anywhere on the map to inspect the parcel under that point and nearby parcels.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !url || !parcelInspectorEnabled) {
      if (contextLayerGroupRef.current) {
        try { contextLayerGroupRef.current.clearLayers(); } catch (_) {}
      }
      if (map && !parcelInspectorEnabled) {
        try { map.closePopup(); } catch (_) {}
      }
      return undefined;
    }

    ensureFeatureGroup(map, contextLayerGroupRef);

    const handleContextMenu = async (event) => {
      if (!event?.latlng) return;
      if (event.originalEvent) L.DomEvent.preventDefault(event.originalEvent);

      const latlng = event.latlng;
      const siteApns = getCurrentSiteApns(countyLines, jobsite);
      const cacheKey = `${latlng.lat.toFixed(6)},${latlng.lng.toFixed(6)}`;
      const reverseCacheKey = getPointReverseGeocodeCacheKey(latlng);
      const cached = parcelPointContextCacheRef.current[cacheKey];
      const cachedPointReverse = pointReverseGeocodeCacheRef.current[reverseCacheKey];
      const pointReversePromise = cachedPointReverse
        ? Promise.resolve(cachedPointReverse)
        : fetchPointReverseGeocode(url, latlng)
          .then((data) => {
            setCachedPointReverseGeocode(pointReverseGeocodeCacheRef, reverseCacheKey, data);
            return data;
          })
          .catch((err) => ({
            success: false,
            message: err?.response?.data?.message || err?.message || 'Point reverse geocode failed',
          }));
      const popup = L.popup({ maxWidth: 440, closeButton: false })
        .setLatLng(latlng)
        .setContent(buildParcelPointContextPopup(cached, parcelContextCacheRef.current, siteApns, {
          loading: !cached,
          latlng,
          pointReverseGeocode: cachedPointReverse,
        }))
        .openOn(map);
      wireParcelPopupActions(popup, cached, parcelContextCacheRef.current, latlng, cachedPointReverse);

      if (cached) {
        rememberParcelIntelLabel(parcelIntelLabelsRef.current, cached, parcelContextCacheRef.current, latlng, cachedPointReverse);
        renderParcelPointHighlights(contextLayerGroupRef.current, cached, siteApns, parcelContextCacheRef.current, cachedPointReverse, parcelIntelLabelsRef.current);
        const cachedApn = cached.clickedParcel?.apn;
        if (cachedApn && !parcelContextCacheRef.current[cachedApn]) {
          loadParcelDetailContext({
            url,
            apn: cachedApn,
            latlng,
            popup,
            pointContext: cached,
            siteApns,
            parcelContextCacheRef,
            contextLayerGroupRef,
            pointReversePromise,
            parcelIntelLabelsRef,
          });
        } else {
          wireParcelPopupActions(popup, cached, parcelContextCacheRef.current, latlng, cachedPointReverse);
        }
        pointReversePromise.then((pointReverseGeocode) => {
          rememberParcelIntelLabel(parcelIntelLabelsRef.current, cached, parcelContextCacheRef.current, latlng, pointReverseGeocode);
          popup.setContent(buildParcelPointContextPopup(cached, parcelContextCacheRef.current, siteApns, {
            latlng,
            pointReverseGeocode,
          }));
          wireParcelPopupActions(popup, cached, parcelContextCacheRef.current, latlng, pointReverseGeocode);
          renderParcelPointHighlights(contextLayerGroupRef.current, cached, siteApns, parcelContextCacheRef.current, pointReverseGeocode, parcelIntelLabelsRef.current);
        });
        return;
      }

      try {
        const pointContext = await fetchCountyParcelPointContext(latlng);
        parcelPointContextCacheRef.current[cacheKey] = pointContext;
        const latestPointReverse = pointReverseGeocodeCacheRef.current[reverseCacheKey];
        rememberParcelIntelLabel(parcelIntelLabelsRef.current, pointContext, parcelContextCacheRef.current, latlng, latestPointReverse);
        renderParcelPointHighlights(contextLayerGroupRef.current, pointContext, siteApns, parcelContextCacheRef.current, latestPointReverse, parcelIntelLabelsRef.current);
        popup.setContent(buildParcelPointContextPopup(pointContext, parcelContextCacheRef.current, siteApns, {
          latlng,
          pointReverseGeocode: latestPointReverse,
        }));
        wireParcelPopupActions(popup, pointContext, parcelContextCacheRef.current, latlng, latestPointReverse);

        pointReversePromise.then((pointReverseGeocode) => {
          rememberParcelIntelLabel(parcelIntelLabelsRef.current, pointContext, parcelContextCacheRef.current, latlng, pointReverseGeocode);
          popup.setContent(buildParcelPointContextPopup(pointContext, parcelContextCacheRef.current, siteApns, {
            latlng,
            pointReverseGeocode,
          }));
          wireParcelPopupActions(popup, pointContext, parcelContextCacheRef.current, latlng, pointReverseGeocode);
          renderParcelPointHighlights(contextLayerGroupRef.current, pointContext, siteApns, parcelContextCacheRef.current, pointReverseGeocode, parcelIntelLabelsRef.current);
        });

        const apn = pointContext?.clickedParcel?.apn;
        if (apn && !parcelContextCacheRef.current[apn]) {
          loadParcelDetailContext({
            url,
            apn,
            latlng,
            popup,
            pointContext,
            siteApns,
            parcelContextCacheRef,
            contextLayerGroupRef,
            pointReversePromise,
            parcelIntelLabelsRef,
          });
        }
      } catch (err) {
        popup.setContent(buildParcelPointContextPopup(null, parcelContextCacheRef.current, siteApns, {
          latlng,
          error: err?.response?.data?.message || err?.message || 'Unable to load parcel context',
        }));
        wireParcelPopupActions(popup, null, parcelContextCacheRef.current, latlng);
      }
    };

    map.on('contextmenu', handleContextMenu);
    return () => {
      map.off('contextmenu', handleContextMenu);
    };
  }, [mapRef, url, jobsite, countyLines, parcelInspectorEnabled, redrawKey]);

  // Draw a new custom property line — saves to backend
  const saveManualLine = useCallback(async (paths) => {
    if (!url || !jobsite?._id || !paths || paths.length < 3) return null;
    try {
      const resp = await axios.post(
        `${url}/api/customer/manual-property-lines`,
        {
          jobSiteId: jobsite._id,
          name: 'Custom Property Line',
          paths,
        }
      );
      if (resp.data?.success && resp.data?.line) {
        setManualLines(prev => [...prev, resp.data.line]);
        return resp.data.line;
      }
    } catch (err) {
      console.error('saveManualLine error:', err);
    }
    return null;
  }, [url, jobsite]);

  const deleteManualLine = useCallback(async (lineId) => {
    if (!url || !lineId) return;
    try {
      await axios.delete(`${url}/api/customer/manual-property-lines/${lineId}`);
      setManualLines(prev => prev.filter(l => l._id !== lineId));
    } catch (err) {
      console.error('deleteManualLine error:', err);
    }
  }, [url]);

  const renameManualLine = useCallback(async (lineId, newName) => {
    if (!url || !lineId) return;
    try {
      const resp = await axios.put(
        `${url}/api/customer/manual-property-lines/${lineId}`,
        { name: newName }
      );
      if (resp.data?.success) {
        setManualLines(prev => prev.map(l => l._id === lineId ? { ...l, name: newName } : l));
      }
    } catch (err) {
      console.error('renameManualLine error:', err);
    }
  }, [url]);

  const updateManualLinePaths = useCallback(async (lineId, newPaths) => {
    if (!url || !lineId || !newPaths || newPaths.length < 3) return;
    try {
      const resp = await axios.put(
        `${url}/api/customer/manual-property-lines/${lineId}`,
        { paths: newPaths }
      );
      if (resp.data?.success) {
        setManualLines(prev => prev.map(l => l._id === lineId ? { ...l, paths: newPaths } : l));
      }
    } catch (err) {
      console.error('updateManualLinePaths error:', err);
    }
  }, [url]);

  return {
    countyLines,
    manualLines,
    usingFallback,
    countyVisible,
    manualVisible,
    setCountyVisible,
    setManualVisible,
    saveManualLine,
    deleteManualLine,
    renameManualLine,
    updateManualLinePaths,
    reload: loadLines,
  };
}

// ESRI fallback — same as V1
async function fetchESRIFallback(siteId, lat, lng) {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  try {
    const parcelsUrl = 'https://gis.mcassessor.maricopa.gov/arcgis/rest/services/Parcels/MapServer/0/query';
    const params = {
      f: 'geojson',
      geometry: `${lng},${lat}`,
      geometryType: 'esriGeometryPoint',
      inSR: '4326',
      outSR: '4326',
      spatialRel: 'esriSpatialRelIntersects',
      returnGeometry: 'true',
      outFields: '*',
    };
    const qs = Object.entries(params)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    const resp = await axios.get(`${parcelsUrl}?${qs}`);
    const features = resp?.data?.features;
    if (!features?.length) return null;

    const geom = features[0].geometry;
    if (!geom || geom.type !== 'Polygon' || !geom.coordinates?.[0]?.length) return null;

    const paths = geom.coordinates[0].map(([lng2, lat2]) => ({ lat: lat2, lng: lng2 }));
    const props = features[0].properties || {};

    return {
      id: `esri_${siteId}_${props.APN || props.OBJECTID || 'unknown'}`,
      APN: props.APN || '',
      address: props.SitusAddress || props.SITE_ADDR || props.ADDRESS || '',
      paths,
      source: 'esri_fallback',
      properties: props,
    };
  } catch (err) {
    console.warn('ESRI fallback failed:', err?.message || err);
    return null;
  }
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function loadPointReverseGeocodeCache() {
  if (typeof localStorage === 'undefined') return {};
  try {
    const parsed = JSON.parse(localStorage.getItem(POINT_REVERSE_GEOCODE_CACHE_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (err) {
    console.warn('Unable to load point reverse geocode cache:', err);
    return {};
  }
}

function getPointReverseGeocodeCacheKey(latlng) {
  const lat = Number(latlng?.lat);
  const lng = Number(latlng?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
  return `${lat.toFixed(POINT_REVERSE_GEOCODE_CACHE_PRECISION)},${lng.toFixed(POINT_REVERSE_GEOCODE_CACHE_PRECISION)}`;
}

function setCachedPointReverseGeocode(cacheRef, key, data) {
  if (!cacheRef?.current || !key || !data) return;
  const nextItem = {
    ...data,
    cachedAt: new Date().toISOString(),
  };
  cacheRef.current[key] = nextItem;
  try {
    const entries = Object.entries(cacheRef.current)
      .sort((a, b) => String(b[1]?.cachedAt || '').localeCompare(String(a[1]?.cachedAt || '')))
      .slice(0, POINT_REVERSE_GEOCODE_CACHE_MAX_ITEMS);
    cacheRef.current = Object.fromEntries(entries);
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(POINT_REVERSE_GEOCODE_CACHE_KEY, JSON.stringify(cacheRef.current));
  } catch (err) {
    console.warn('Unable to save point reverse geocode cache:', err);
  }
}

function normalizeText(value) {
  return String(value || '').trim().replace(/\s+/g, ' ').toUpperCase();
}

function formatNumber(value, decimals = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return numeric.toLocaleString(undefined, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  });
}

function formatMoney(value) {
  const numeric = Number(String(value || '').replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(numeric) || numeric <= 0) return '';
  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function normalizeApn(value) {
  return String(value || '').trim();
}

async function fetchCountyParcelPointContext(latlng) {
  const clickedFeatures = await fetchESRIParcels({
    geometry: `${latlng.lng},${latlng.lat}`,
    geometryType: 'esriGeometryPoint',
    spatialRel: 'esriSpatialRelIntersects',
    limit: 5,
  });
  const nearbyFeatures = await fetchESRIParcels({
    geometry: buildEnvelope(latlng, 0.0014),
    geometryType: 'esriGeometryEnvelope',
    spatialRel: 'esriSpatialRelIntersects',
    limit: 14,
  });

  const containing = clickedFeatures
    .map(feature => esriFeatureToPointParcel(feature, latlng, 'contains_point'))
    .filter(Boolean);
  const nearbyByApn = new Map();

  containing.forEach((parcel) => {
    if (parcel.apn) nearbyByApn.set(parcel.apn, parcel);
  });

  nearbyFeatures
    .map(feature => esriFeatureToPointParcel(feature, latlng, 'nearby_county'))
    .filter(Boolean)
    .sort((a, b) => (a.distanceM ?? Infinity) - (b.distanceM ?? Infinity))
    .forEach((parcel) => {
      if (parcel.apn && !nearbyByApn.has(parcel.apn)) nearbyByApn.set(parcel.apn, parcel);
    });

  const nearby = Array.from(nearbyByApn.values()).slice(0, 14);
  const clickedParcel = containing[0] || nearby[0] || null;

  return {
    success: true,
    source: 'mcassessor_feature_service',
    point: { lat: latlng.lat, lng: latlng.lng },
    clickedParcel,
    containing,
    nearby,
  };
}

async function fetchPointReverseGeocode(url, latlng) {
  const params = new URLSearchParams({
    lat: latlng.lat,
    lng: latlng.lng,
  });
  const resp = await axios.get(`${url}/api/customer/pointReverseGeocode?${params.toString()}`);
  return resp.data?.reverseGeocode || resp.data || null;
}

async function fetchESRIParcels({ geometry, geometryType, spatialRel, limit }) {
  const parcelsUrl = 'https://gis.mcassessor.maricopa.gov/arcgis/rest/services/Parcels/MapServer/0/query';
  const params = {
    f: 'geojson',
    geometry,
    geometryType,
    inSR: '4326',
    outSR: '4326',
    spatialRel,
    returnGeometry: 'true',
    outFields: '*',
    resultRecordCount: limit,
  };
  const qs = Object.entries(params)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  const resp = await axios.get(`${parcelsUrl}?${qs}`, { timeout: 10000 });
  return resp?.data?.features || [];
}

function buildEnvelope(latlng, delta) {
  return [
    latlng.lng - delta,
    latlng.lat - delta,
    latlng.lng + delta,
    latlng.lat + delta,
  ].join(',');
}

function esriFeatureToPointParcel(feature, clickLatLng, matchType) {
  const props = feature?.properties || {};
  const apn = normalizeApn(props.APN || props.apn || props.PARCEL || props.PARCELID || props.PIN);
  const paths = geoJsonPolygonToPaths(feature?.geometry);
  if (!apn && !paths.length) return null;
  const center = getPathCenter(paths);
  return {
    id: apn || props.OBJECTID || props.FID || '',
    apn,
    APN: apn,
    address: props.SitusAddress || props.SITE_ADDR || props.ADDRESS || props.PropertyAddress || '',
    acres: numericOrNull(props.acres || props.Acres || props.ACRES || props.Shape__Area),
    sf: numericOrNull(props.sf || props.SF || props.Shape__Area),
    centroid: center ? { type: 'Point', coordinates: [center.lng, center.lat] } : null,
    distanceM: center ? distanceMeters(clickLatLng, center) : null,
    matchType,
    paths,
    properties: props,
  };
}

function geoJsonPolygonToPaths(geometry) {
  if (!geometry?.coordinates?.length) return [];
  const ring = geometry.type === 'MultiPolygon'
    ? geometry.coordinates?.[0]?.[0]
    : geometry.coordinates?.[0];
  if (!Array.isArray(ring)) return [];
  return ring
    .filter(point => Array.isArray(point) && point.length >= 2)
    .map(([lng, lat]) => ({ lat, lng }));
}

function getPathCenter(paths) {
  if (!paths?.length) return null;
  const sum = paths.reduce((acc, point) => ({
    lat: acc.lat + Number(point.lat || 0),
    lng: acc.lng + Number(point.lng || 0),
  }), { lat: 0, lng: 0 });
  return {
    lat: sum.lat / paths.length,
    lng: sum.lng / paths.length,
  };
}

function numericOrNull(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function distanceMeters(a, b) {
  const earthRadiusM = 6371000;
  const toRad = degrees => degrees * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadiusM * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function getCurrentSiteApns(countyLines, jobsite) {
  const apns = new Set();
  countyLines.forEach((line) => {
    const apn = normalizeApn(line.APN);
    if (apn) apns.add(apn);
  });
  [jobsite?.apn, jobsite?.APN].forEach((value) => {
    const apn = normalizeApn(value);
    if (apn) apns.add(apn);
  });
  return apns;
}

function formatDistanceFeet(distanceM) {
  const numeric = Number(distanceM);
  if (!Number.isFinite(numeric)) return '';
  return `${Math.round(numeric * 3.28084).toLocaleString()} ft`;
}

function getParcelApn(parcel) {
  return parcel?.apn || parcel?.APN || '';
}

function parcelAddress(parcel, summary) {
  return summary?.situsAddress || parcel?.address || '';
}

async function loadParcelDetailContext({
  url,
  apn,
  latlng,
  popup,
  pointContext,
  siteApns,
  parcelContextCacheRef,
  contextLayerGroupRef,
  pointReversePromise,
  parcelIntelLabelsRef,
}) {
  try {
    const params = new URLSearchParams({ apn });
    if (latlng) {
      params.set('lat', latlng.lat);
      params.set('lng', latlng.lng);
    }
    const contextResp = await axios.get(`${url}/api/customer/parcelContext?${params.toString()}`);
    parcelContextCacheRef.current[apn] = contextResp.data;
    const pointReverseGeocode = await Promise.resolve(pointReversePromise).catch(() => null);
    rememberParcelIntelLabel(parcelIntelLabelsRef?.current, pointContext, parcelContextCacheRef.current, latlng, pointReverseGeocode);
    popup.setContent(buildParcelPointContextPopup(pointContext, parcelContextCacheRef.current, siteApns, {
      latlng,
      pointReverseGeocode,
    }));
    wireParcelPopupActions(popup, pointContext, parcelContextCacheRef.current, latlng, pointReverseGeocode);
    renderParcelPointHighlights(contextLayerGroupRef?.current, pointContext, siteApns, parcelContextCacheRef.current, pointReverseGeocode, parcelIntelLabelsRef?.current);
  } catch (err) {
    popup.setContent(buildParcelPointContextPopup(pointContext, parcelContextCacheRef.current, siteApns, {
      latlng,
      detailError: err?.response?.data?.message || err?.message || 'Owner lookup unavailable',
    }));
    wireParcelPopupActions(popup, pointContext, parcelContextCacheRef.current, latlng);
  }
}

function renderParcelPointHighlights(group, pointContext, siteApns, parcelContextCache = {}, pointReverseGeocode = null, parcelIntelLabels = {}) {
  if (!group) return;
  group.clearLayers();
  const clickedApn = getParcelApn(pointContext?.clickedParcel);
  const parcels = pointContext?.nearby || [];

  parcels.forEach((parcel) => {
    if (!parcel?.paths?.length) return;
    const apn = getParcelApn(parcel);
    const isClicked = apn && apn === clickedApn;
    const isSiteParcel = apn && siteApns.has(apn);
    const color = isSiteParcel ? '#2e7d32' : isClicked ? '#f57c00' : '#1976d2';
    const fillOpacity = isClicked ? 0.18 : isSiteParcel ? 0.08 : 0.06;
    const coords = parcel.paths.map(p => [p.lat, p.lng]);
    const layer = L.polygon(coords, {
      color,
      weight: isClicked ? 4 : 2,
      opacity: isClicked ? 0.95 : 0.65,
      fillColor: color,
      fillOpacity,
      interactive: false,
      dashArray: isSiteParcel ? undefined : '6, 4',
    });
    layer.bindTooltip(`${isSiteParcel ? 'Selected site' : 'Nearby parcel'}${apn ? `: ${apn}` : ''}`);
    group.addLayer(layer);

    const context = parcelContextCache?.[apn] || {};
    const summary = context.summary || null;
    if (isClicked && summary) {
      rememberParcelIntelLabel(parcelIntelLabels, pointContext, parcelContextCache, pointContext?.point, pointReverseGeocode);
    }
  });

  Object.values(parcelIntelLabels || {}).forEach((intel) => {
    if (!Number.isFinite(Number(intel?.lat)) || !Number.isFinite(Number(intel?.lng))) return;
    const labelHtml = buildParcelIntelLabel(intel);
    if (!labelHtml) return;
    const marker = L.marker([Number(intel.lat), Number(intel.lng)], {
      interactive: false,
      icon: L.divIcon({
        className: '',
        html: labelHtml,
        iconSize: [240, 58],
        iconAnchor: [120, 58],
      }),
    });
    group.addLayer(marker);
  });
}

function getReverseAddress(reverseGeocode) {
  return reverseGeocode?.pointAddress ||
    reverseGeocode?.pointPlace?.address ||
    '';
}

function getPointPlaceName(reverseGeocode) {
  return reverseGeocode?.pointPlace?.name || '';
}

function getNearestAddress(reverseGeocode) {
  return reverseGeocode?.nearestAddress ||
    reverseGeocode?.address ||
    reverseGeocode?.results?.[0]?.formatted_address ||
    '';
}

function formatNearbyAddress(address, distanceM) {
  if (!address) return '';
  const distance = Number(distanceM);
  if (!Number.isFinite(distance)) return address;
  return `${address} (${Math.round(distance * 3.28084)} ft away)`;
}

function rememberParcelIntelLabel(labelCache, pointContext, parcelContextCache, latlng, pointReverseGeocode = null) {
  if (!labelCache || !pointContext) return;
  const clicked = pointContext.clickedParcel;
  const apn = getParcelApn(clicked);
  if (!apn) return;
  const context = parcelContextCache?.[apn] || {};
  const summary = context.summary || {};
  const owner = summary.owner || '';
  const pointName = getPointPlaceName(pointReverseGeocode);
  const pointAddress = getReverseAddress(pointReverseGeocode) || getReverseAddress(context.reverseGeocode);
  const address = pointAddress || '';
  const lat = latlng?.lat ?? pointContext?.point?.lat;
  const lng = latlng?.lng ?? pointContext?.point?.lng;
  if (!pointName && !address) return;
  if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return;
  const labelKey = `${apn}:${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;
  labelCache[labelKey] = {
    apn,
    owner,
    pointName,
    address,
    addressConfidence: pointAddress ? 'point' : 'unknown',
    distanceM: pointReverseGeocode?.reverseDistanceM ?? null,
    lat: Number(lat),
    lng: Number(lng),
  };
}

function buildParcelIntelLabel(intel = {}) {
  const pointName = intel.pointName || '';
  const owner = intel.owner || '';
  const address = intel.address || '';
  const title = pointName || owner;
  if (!title && !address) return '';
  return `
    <div style="
      background:rgba(255,255,255,0.92);
      border:1px solid rgba(0,0,0,0.18);
      border-radius:4px;
      box-shadow:0 1px 4px rgba(0,0,0,0.25);
      color:#2f2f2f;
      font-family:Arial,sans-serif;
      font-size:11px;
      line-height:1.2;
      max-width:220px;
      padding:4px 6px;
      text-align:center;
      white-space:normal;
    ">
      ${title ? `<div style="font-weight:700;">${escapeHtml(title)}</div>` : ''}
      ${address ? `<div>${escapeHtml(address)}</div>` : ''}
    </div>
  `;
}

function saveIconSvg(saved = false) {
  const color = saved ? '#2e7d32' : '#555';
  return `
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <path fill="${color}" d="M17 3H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 1.99 2H19c1.1 0 2-.9 2-2V7l-4-4zM12 19c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zM6 8V5h9v3H6z" />
    </svg>
  `;
}

function closeIconSvg() {
  return `
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <path fill="#555" d="M18.3 5.71 12 12l6.3 6.29-1.41 1.41L10.59 13.41 4.29 19.71 2.88 18.3 9.17 12 2.88 5.71 4.29 4.29 10.59 10.59 16.89 4.29z" />
    </svg>
  `;
}

function popupCloseButton() {
  return `
    <button
      type="button"
      data-site-takeoff-close-parcel="1"
      title="Close"
      aria-label="Close"
      style="
        align-items:center;
        background:#fff;
        border:1px solid #d0d0d0;
        border-radius:4px;
        cursor:pointer;
        display:inline-flex;
        height:24px;
        justify-content:center;
        padding:3px;
        width:24px;
      "
    >${closeIconSvg()}</button>
  `;
}

function linkIconSvg(type) {
  const paths = {
    assessor: '<path fill="currentColor" d="M4 20h16v-2H4v2Zm2-4h2V9H6v7Zm5 0h2V9h-2v7Zm5 0h2V9h-2v7ZM3 7h18L12 2 3 7Z" />',
    maps: '<path fill="currentColor" d="m15 5-6 2.11V19l6-2.11V5Zm2 0v11.89l3 1.5V6.5L17 5ZM4 7.11V19l3-1.5V5.61l-3 1.5Z" />',
    pin: '<path fill="currentColor" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z" />',
    earth: '<path fill="currentColor" d="M12 2a10 10 0 1 0 .01 0ZM4.26 11h3.78a15.9 15.9 0 0 1 .7-4.03A8.03 8.03 0 0 0 4.26 11Zm0 2a8.03 8.03 0 0 0 4.48 4.03A15.9 15.9 0 0 1 8.04 13H4.26ZM12 19.5c-.7-1.01-1.5-3.14-1.86-6.5h3.72c-.36 3.36-1.16 5.49-1.86 6.5ZM14.05 11h-4.1C10.31 7.64 11.1 5.51 12 4.5c.9 1.01 1.69 3.14 2.05 6.5Zm1.21 6.03A8.03 8.03 0 0 0 19.74 13h-3.78a15.9 15.9 0 0 1-.7 4.03ZM15.96 11h3.78a8.03 8.03 0 0 0-4.48-4.03c.36 1.15.6 2.52.7 4.03Z" />',
    search: '<path fill="currentColor" d="m9.5 3a6.5 6.5 0 0 1 5.14 10.48l4.44 4.44-1.41 1.41-4.44-4.44A6.5 6.5 0 1 1 9.5 3Zm0 2a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9Z" />',
  };
  return `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">${paths[type] || paths.pin}</svg>`;
}

function iconLink(url, label, iconType) {
  if (!url) return '';
  return `
    <a
      href="${escapeHtml(url)}"
      target="_blank"
      rel="noopener noreferrer"
      title="${escapeHtml(label)}"
      aria-label="${escapeHtml(label)}"
      style="
        align-items:center;
        background:#fff;
        border:1px solid #d0d0d0;
        border-radius:4px;
        color:#444;
        display:inline-flex;
        height:28px;
        justify-content:center;
        text-decoration:none;
        width:28px;
      "
    >${linkIconSvg(iconType)}</a>
  `;
}

function buildSaveParcelPayload(pointContext, parcelContextCache, latlng, pointReverseGeocode = null) {
  const clicked = pointContext?.clickedParcel;
  const apn = getParcelApn(clicked);
  if (!apn) return null;
  const context = parcelContextCache?.[apn] || {};
  const summary = context.summary || {};
  const pointAddress = getReverseAddress(pointReverseGeocode) || getReverseAddress(context.reverseGeocode);
  const nearestAddress = getNearestAddress(pointReverseGeocode);
  const pointName = getPointPlaceName(pointReverseGeocode);
  return {
    apn,
    owner: summary.owner || '',
    pointName,
    parcelAddress: parcelAddress(clicked, summary),
    pointAddress,
    nearestAddress,
    acres: summary.acres ?? clicked?.acres ?? null,
    sf: summary.sf ?? clicked?.sf ?? null,
    lat: latlng?.lat ?? pointContext?.point?.lat ?? null,
    lng: latlng?.lng ?? pointContext?.point?.lng ?? null,
    source: pointContext?.source || 'site_takeoff_v3_context',
    savedAt: new Date().toISOString(),
  };
}

function saveParcelIntel(payload) {
  if (!payload?.apn) return false;
  try {
    const existing = JSON.parse(localStorage.getItem(SAVED_PARCEL_INTEL_KEY) || '[]');
    const next = [
      payload,
      ...existing.filter(item => item?.apn !== payload.apn),
    ].slice(0, 100);
    localStorage.setItem(SAVED_PARCEL_INTEL_KEY, JSON.stringify(next));
    return true;
  } catch (err) {
    console.warn('Unable to save parcel intel:', err);
    return false;
  }
}

function wireParcelPopupActions(popup, pointContext, parcelContextCache, latlng, pointReverseGeocode = null) {
  setTimeout(() => {
    const root = popup?.getElement?.();
    if (!root) return;
    const closeButton = root.querySelector('[data-site-takeoff-close-parcel]');
    if (closeButton && closeButton.dataset.bound !== '1') {
      closeButton.dataset.bound = '1';
      closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        popup?.remove?.();
      });
    }
    const button = root.querySelector('[data-site-takeoff-save-parcel]');
    if (!button || button.dataset.bound === '1') return;
    button.dataset.bound = '1';
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const payload = buildSaveParcelPayload(pointContext, parcelContextCache, latlng, pointReverseGeocode);
      const saved = saveParcelIntel(payload);
      if (saved) {
        button.setAttribute('aria-label', 'Saved prospect');
        button.setAttribute('title', 'Saved prospect');
        button.innerHTML = saveIconSvg(true);
      }
    });
  }, 0);
}

function buildNearbyParcelRows(pointContext, parcelContextCache, siteApns) {
  const clickedApn = getParcelApn(pointContext?.clickedParcel);
  const parcels = (pointContext?.nearby || [])
    .filter(parcel => getParcelApn(parcel) && getParcelApn(parcel) !== clickedApn)
    .slice(0, 8);

  if (!parcels.length) {
    return '<div style="font-size:12px;color:#777;margin-top:6px;">No nearby parcels found in the lookup radius.</div>';
  }

  return parcels.map((parcel) => {
    const apn = getParcelApn(parcel);
    const summary = getCachedSummary(parcelContextCache, apn);
    const isSiteParcel = siteApns.has(apn);
    const label = isSiteParcel ? 'site' : 'not site';
    const color = isSiteParcel ? '#2e7d32' : '#1976d2';
    const distance = formatDistanceFeet(parcel.distanceM);
    return `
      <div style="border-top:1px solid #eee;padding:6px 0;">
        <div style="display:flex;justify-content:space-between;gap:8px;">
          <div style="font-size:12px;font-weight:700;">${escapeHtml(apn)}</div>
          <div style="font-size:11px;color:${color};font-weight:700;text-transform:uppercase;">${escapeHtml(label)}</div>
        </div>
        <div style="font-size:11px;color:#666;">${escapeHtml(parcelAddress(parcel, summary) || 'No address')}</div>
        ${distance ? `<div style="font-size:11px;color:#777;">${escapeHtml(distance)} from click</div>` : ''}
      </div>
    `;
  }).join('');
}

function buildParcelPointContextPopup(pointContext, parcelContextCache, siteApns, options = {}) {
  const clicked = pointContext?.clickedParcel;
  const clickedApn = getParcelApn(clicked);
  const summary = getCachedSummary(parcelContextCache, clickedApn) || {};
  const isSiteParcel = clickedApn && siteApns.has(clickedApn);
  const reverseAddress = getReverseAddress(options.pointReverseGeocode) ||
    getReverseAddress(parcelContextCache?.[clickedApn]?.reverseGeocode);
  const nearestAddress = getNearestAddress(options.pointReverseGeocode);
  const reverseDistance = options.pointReverseGeocode?.reverseDistanceM;
  const pointName = getPointPlaceName(options.pointReverseGeocode) ||
    getPointPlaceName(parcelContextCache?.[clickedApn]?.reverseGeocode);
  const status = clickedApn
    ? isSiteParcel ? 'Selected site parcel' : 'Outside selected site'
    : 'No parcel matched';
  const statusColor = isSiteParcel ? '#2e7d32' : clickedApn ? '#f57c00' : '#777';
  const address = parcelAddress(clicked, summary);
  const acres = summary.acres ?? clicked?.acres;
  const sf = summary.sf ?? clicked?.sf;
  const assessorUrl = clickedApn
    ? `https://mcassessor.maricopa.gov/parcel/${encodeURIComponent(clickedApn)}`
    : '';
  const lat = options.latlng?.lat;
  const lng = options.latlng?.lng;
  const googleUrl = lat && lng
    ? `https://www.google.com/maps/@${encodeURIComponent(lat)},${encodeURIComponent(lng)},19z`
    : '';
  const whatsHereQuery = reverseAddress || (lat && lng ? `${lat},${lng}` : '');
  const googleWhatsHereUrl = whatsHereQuery
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(whatsHereQuery)}`
    : '';
  const googleEarthUrl = lat && lng
    ? `https://earth.google.com/web/@${encodeURIComponent(lat)},${encodeURIComponent(lng)},150a,150d,35y,0h,60t,0r`
    : '';
  const googleSearchTerms = [
    pointName,
    summary.owner,
    reverseAddress || nearestAddress || address || (lat && lng ? `${lat},${lng}` : ''),
  ].filter(Boolean).join(' ');
  const googleSearchUrl = googleSearchTerms
    ? `https://www.google.com/search?q=${encodeURIComponent(googleSearchTerms)}`
    : '';
  const clickedPoint = lat && lng ? `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}` : '';

  if (options.loading) {
    return `
      <div style="min-width:320px;max-width:420px;">
        <div style="display:flex;justify-content:space-between;gap:12px;align-items:center;margin-bottom:6px;">
          <div style="font-size:13px;font-weight:700;">What is here?</div>
          ${popupCloseButton()}
        </div>
        <div style="font-size:12px;color:#666;">Looking up parcel at this point...</div>
      </div>
    `;
  }

  if (options.error) {
    return `
      <div style="min-width:320px;max-width:420px;">
        <div style="display:flex;justify-content:space-between;gap:12px;align-items:center;margin-bottom:6px;">
          <div style="font-size:13px;font-weight:700;">What is here?</div>
          ${popupCloseButton()}
        </div>
        <div style="font-size:12px;color:#b00020;">${escapeHtml(options.error)}</div>
      </div>
    `;
  }

  return `
    <div style="min-width:340px;max-width:420px;">
      <div style="display:flex;justify-content:space-between;gap:12px;align-items:center;margin-bottom:8px;">
        <div style="font-size:13px;font-weight:700;">What is here?</div>
        <div style="display:flex;align-items:center;gap:8px;">
          <div style="font-size:11px;color:${statusColor};font-weight:700;text-transform:uppercase;">${escapeHtml(status)}</div>
          ${clickedApn ? `
            <button
              type="button"
              data-site-takeoff-save-parcel="1"
              title="Save prospect"
              aria-label="Save prospect"
              style="
                align-items:center;
                background:#fff;
                border:1px solid #d0d0d0;
                border-radius:4px;
                cursor:pointer;
                display:inline-flex;
                height:24px;
                justify-content:center;
                padding:3px;
                width:24px;
              "
            >${saveIconSvg(false)}</button>
          ` : ''}
          ${popupCloseButton()}
        </div>
      </div>
      ${row('Parcel APN', clickedApn || 'No parcel found')}
      ${row('Parcel owner', summary.owner || (clickedApn ? 'Loading...' : ''))}
      ${row('Parcel address', address)}
      ${row('Clicked point', clickedPoint)}
      ${row('Possible place', pointName)}
      ${row('Point address', reverseAddress)}
      ${row('Nearby address', !reverseAddress && nearestAddress ? formatNearbyAddress(nearestAddress, reverseDistance) : '')}
      ${row('Acres', acres ? formatNumber(acres, 2) : '')}
      ${row('Sq ft', sf ? formatNumber(sf) : '')}
      ${row('Match', clicked?.matchType === 'contains_point' ? 'Point is inside this parcel' : clicked?.matchType === 'nearest_centroid' ? 'Nearest parcel centroid' : clicked?.matchType === 'nearby_county' ? 'Nearby county parcel' : '')}
      ${options.detailError ? `<div style="margin-top:6px;color:#b00020;font-size:12px;">${escapeHtml(options.detailError)}</div>` : ''}
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:10px;">
        ${iconLink(assessorUrl, 'MC Assessor', 'assessor')}
        ${iconLink(googleUrl, 'Google Maps', 'maps')}
        ${iconLink(googleWhatsHereUrl, "Google What's Here", 'pin')}
        ${iconLink(googleEarthUrl, 'Google Earth', 'earth')}
        ${iconLink(googleSearchUrl, 'Google Search', 'search')}
      </div>
    </div>
  `;
}

function getCachedSummary(cache, apn) {
  return cache?.[apn]?.summary || null;
}

function getLineAddress(line, summary) {
  return summary?.situsAddress || line.address || line.properties?.address || '';
}

function getRelatedParcelSummary(line, countyLines, cache, currentSummary) {
  const currentAddress = normalizeText(getLineAddress(line, currentSummary));
  const currentOwner = normalizeText(currentSummary?.owner);
  const sameAddress = [];
  const sameOwner = [];

  countyLines.forEach((other) => {
    if (!other.APN || other.APN === line.APN) return;
    const otherSummary = getCachedSummary(cache, other.APN);
    const otherAddress = normalizeText(getLineAddress(other, otherSummary));
    const otherOwner = normalizeText(otherSummary?.owner);
    if (currentAddress && otherAddress === currentAddress) sameAddress.push(other.APN);
    if (currentOwner && otherOwner === currentOwner) sameOwner.push(other.APN);
  });

  return { sameAddress, sameOwner };
}

function row(label, value) {
  if (value === null || value === undefined || value === '') return '';
  return `
    <div style="display:flex;gap:8px;margin:3px 0;">
      <div style="width:94px;color:#666;font-size:11px;">${escapeHtml(label)}</div>
      <div style="flex:1;font-size:12px;font-weight:500;">${escapeHtml(value)}</div>
    </div>
  `;
}

function buildParcelContextPopup(line, countyLines, cache, options = {}) {
  const context = cache?.[line.APN] || {};
  const summary = context.summary || {};
  const reverseAddress = context.reverseGeocode?.address || context.reverseGeocode?.results?.[0]?.formatted_address || '';
  const related = getRelatedParcelSummary(line, countyLines, cache, summary);
  const address = getLineAddress(line, summary);
  const acres = summary.acres ?? line.acres;
  const sf = summary.sf ?? line.sf;
  const salePrice = formatMoney(summary.salePrice);
  const lat = options.latlng?.lat;
  const lng = options.latlng?.lng;
  const assessorUrl = `https://mcassessor.maricopa.gov/parcel/${encodeURIComponent(line.APN)}`;
  const assessorMapUrl = lat && lng
    ? `https://maps.mcassessor.maricopa.gov/ipa.aspx?1=${encodeURIComponent(lat)}&2=${encodeURIComponent(lng)}&a=${encodeURIComponent(address)}`
    : assessorUrl;
  const googleUrl = lat && lng
    ? `https://www.google.com/maps/@${encodeURIComponent(lat)},${encodeURIComponent(lng)},19z`
    : '';

  return `
    <div style="min-width:280px;max-width:340px;">
      <div style="font-size:13px;font-weight:700;margin-bottom:6px;">Parcel Context</div>
      ${row('APN', line.APN)}
      ${row('Owner', summary.owner || (options.loading ? 'Loading...' : ''))}
      ${row('Address', address)}
      ${row('Nominatim', reverseAddress)}
      ${row('Acres', acres ? formatNumber(acres, 2) : '')}
      ${row('Sq ft', sf ? formatNumber(sf) : '')}
      ${row('Lot', summary.lot)}
      ${row('Subdivision', summary.subdivision)}
      ${row('Land use', summary.landUse)}
      ${row('Sale', salePrice)}
      ${row('Sale date', summary.saleDate)}
      ${row('Same address', related.sameAddress.length ? related.sameAddress.join(', ') : '')}
      ${row('Same owner', related.sameOwner.length ? related.sameOwner.join(', ') : '')}
      ${options.error ? `<div style="margin-top:6px;color:#b00020;font-size:12px;">${escapeHtml(options.error)}</div>` : ''}
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:10px;">
        <a href="${escapeHtml(assessorUrl)}" target="_blank" rel="noopener noreferrer" style="font-size:12px;">MC Assessor</a>
        <a href="${escapeHtml(assessorMapUrl)}" target="_blank" rel="noopener noreferrer" style="font-size:12px;">MC Map</a>
        ${googleUrl ? `<a href="${escapeHtml(googleUrl)}" target="_blank" rel="noopener noreferrer" style="font-size:12px;">Google Maps</a>` : ''}
      </div>
    </div>
  `;
}
